import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Api } from "./Data/Api";
import { IntegrationStatus } from "./Data/ApiTransport";
import { Subscription, SubscriptionParker, Vehicle } from "./Data/ApiTypes";
import {
  ServerRequestsParkerIntegrationPartyDto,
  ParkerIntegrationPartyApi,
} from "./shared/api/axios-client";
import { createApiService } from "./shared/apiConfig";

const VEHICLE_INTEGRATION_ENROLL_ERROR =
  "The vehicle could not be enabled. Please ensure the account RFID is activated.";

export function useSubscriptionVehiclesAndIntegrations({
  facilityId,
  parkerId,
  subscriptionId,
  vehicles,
  setSub,
  setParker,
  setParkers,
  setParkerIntegrations,
  setChooseModalVehicles,
  setChooseModalExclude,
  setShowChoose,
}: {
  facilityId: number;
  parkerId: number;
  subscriptionId: number;
  vehicles: Vehicle[];
  setSub?: React.Dispatch<React.SetStateAction<Subscription>>;
  setParker?: React.Dispatch<
    React.SetStateAction<SubscriptionParker | undefined>
  >;
  setParkers?: React.Dispatch<React.SetStateAction<any[]>>;
  setParkerIntegrations?: React.Dispatch<
    React.SetStateAction<ServerRequestsParkerIntegrationPartyDto[]>
  >;
  setChooseModalVehicles: (v: Vehicle[]) => void;
  setChooseModalExclude: (v: number[]) => void;
  setShowChoose: (v: boolean) => void;
}) {
  const [accountVehicles, setAccountVehicles] = useState<Vehicle[]>([]);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const result = await Api.vehicleListByParker(parkerId, facilityId);
      if (!cancelled) {
        setAccountVehicles(result.isOk ? result.value : []);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [parkerId, facilityId]);

  const canAddExisting = useMemo(() => {
    const linkedIds = new Set(vehicles.map((v) => v.vehicleId));
    return accountVehicles.some((v) => !linkedIds.has(v.vehicleId));
  }, [accountVehicles, vehicles]);

  const refreshParkerIntegrations = async () => {
    if (!setParkerIntegrations) return;

    try {
      const api = createApiService(ParkerIntegrationPartyApi);
      const response = await api.listParkerIntegrationParties({
        subscriptionId,
        parkerId,
      });
      setParkerIntegrations(response.data.items ?? []);
    } catch (err) {
      console.error("Failed to refresh parker integrations:", err);
    }
  };

  const vehicleLink = async (vehicle: Vehicle) => {
    const addToSub = await Api.subscriptionVehicleAdd(
      facilityId,
      subscriptionId,
      { vehicleId: vehicle.vehicleId }
    );

    if (!addToSub.isOk) {
      toast.error(addToSub.error);
      return;
    }

    if (setSub) {
      setSub((prev) => ({
        ...prev,
        vehicles: [...prev.vehicles, vehicle],
      }));
    }

    if (setParkers) {
      setParkers((prevParkers) =>
        prevParkers.map((p) =>
          p.parker.parkerId === parkerId
            ? {
                ...p,
                vehicles: [...p.vehicles, vehicle],
                parker: {
                  ...p.parker,
                  vehicles: [...(p.parker?.vehicles ?? []), vehicle],
                },
              }
            : p
        )
      );
    }

    if (setParker) {
      setParker((prev) => {
        if (!prev) return prev;

        const updatedVehicles = [...prev.vehicles, vehicle];

        return {
          ...prev,
          vehicles: updatedVehicles,
          parker: {
            ...prev.parker,
            vehicles: updatedVehicles,
          },
        };
      });
    }

    await refreshParkerIntegrations();

    toast.success("Vehicle Added Successfully");
  };

  const vehicleAdd = async (data: Vehicle) => {
    const addRes = await Api.vehicleAdd(facilityId, parkerId, data);

    if (!addRes.isOk) {
      toast.error(addRes.error);
      return;
    }

    setAccountVehicles((prev) =>
      prev.some((v) => v.vehicleId === addRes.value.vehicleId)
        ? prev
        : [...prev, addRes.value]
    );

    await vehicleLink(addRes.value);
  };

  const vehicleRemove = async (vehicle: Vehicle) => {
    const deleteRes = await Api.subscriptionVehicleDelete(
      facilityId,
      subscriptionId,
      vehicle.vehicleId,
      { vehicleId: vehicle.vehicleId }
    );

    if (!deleteRes.isOk) {
      toast.error(deleteRes.error as string);
      return;
    }

    if (setSub) {
      setSub((prev) => ({
        ...prev,
        vehicles: prev.vehicles.filter(
          (v) => v.vehicleId !== vehicle.vehicleId
        ),
      }));
    }

    if (setParkers) {
      setParkers((prevParkers) =>
        prevParkers.map((p) => {
          if (p.parker.parkerId === parkerId) {
            const updatedVehicles = p.vehicles?.filter(
              (v: any) => v.vehicleId !== vehicle.vehicleId
            );

            return {
              ...p,
              vehicles: updatedVehicles,
              lpn:
                updatedVehicles.length > 0
                  ? updatedVehicles
                      .map((v: any) => v.licensePlateNumber)
                      .join(" | ")
                  : "N/A",
              parker: {
                ...p.parker,
                vehicles: updatedVehicles,
              },
            };
          }

          return p;
        })
      );
    }

    if (setParker) {
      setParker((prev) =>
        prev
          ? {
              ...prev,
              vehicles: prev.vehicles?.filter(
                (v) => v.vehicleId !== vehicle.vehicleId
              ),
              parker: {
                ...prev.parker,
                vehicles: prev.parker.vehicles?.filter(
                  (v) => v.vehicleId !== vehicle.vehicleId
                ),
              },
            }
          : prev
      );
    }

    await refreshParkerIntegrations();

    toast.success("Vehicle Removed Successfully");
  };

  const openChooseModalForParkersList = async () => {
    const result = await Api.vehicleListByParker(parkerId, facilityId);
    const list = result.isOk ? result.value : [];

    setAccountVehicles(list);
    setChooseModalVehicles(list);

    setChooseModalExclude(vehicles.map((v) => v.vehicleId));

    setShowChoose(true);
  };

  const handleSubscriptionIntegration = async (
    integrationPartyId: number,
    subscriptionId: number,
    parkerId: number,
    newStatus: IntegrationStatus
  ) => {
    const api = createApiService(ParkerIntegrationPartyApi);

    const response =
      newStatus === IntegrationStatus.Activated
        ? await api.parkerIntegrationPartiesSubscriptionEnrollPatch({
            serverRequestsEnrollSubscriptionIntegrationPartiesRequest: {
              subscriptionId,
              parkerId: parkerId || parkerId,
              facilityId,
              integrationPartyId,
            },
          })
        : await api.parkerIntegrationPartiesSubscriptionUnrollPatch({
            serverRequestsUnrollSubscriptionIntegrationPartiesRequest: {
              subscriptionId,
              parkerId: parkerId || parkerId,
              facilityId,
              integrationPartyId,
            },
          });

    return response.data.parkerIntegrationParty;
  };

  const handleVehicleIntegration = async (
    integrationPartyId: number,
    subscriptionId: number,
    parkerId: number,
    vehicleId: number,
    newStatus: IntegrationStatus
  ) => {
    const api = createApiService(ParkerIntegrationPartyApi);

    const response =
      newStatus === IntegrationStatus.Activated
        ? await api.parkerIntegrationPartiesVehicleEnrollPatch({
            serverRequestsEnrollVehicleIntegrationPartiesRequest: {
              subscriptionId,
              parkerId: parkerId || parkerId,
              facilityId,
              integrationPartyId,
              vehicleId,
            },
          })
        : await api.parkerIntegrationPartiesVehicleUnrollPatch({
            serverRequestsUnrollVehicleIntegrationPartiesRequest: {
              subscriptionId,
              parkerId: parkerId || parkerId,
              facilityId,
              integrationPartyId,
              vehicleId,
            },
          });

    return response.data.parkerIntegrationParty;
  };

  const handleToggleIntegration = async (
    integrationPartyId: number,
    subscriptionId: number,
    parkerId: number,
    vehicleId: number | undefined,
    newStatus: IntegrationStatus
  ) => {
    const isVehicleEnroll =
      vehicleId != null && newStatus === IntegrationStatus.Activated;

    try {
      const updatedIntegration = vehicleId
        ? await handleVehicleIntegration(
            integrationPartyId,
            subscriptionId,
            parkerId,
            vehicleId,
            newStatus
          )
        : await handleSubscriptionIntegration(
            integrationPartyId,
            subscriptionId,
            parkerId,
            newStatus
          );

      if (!updatedIntegration) {
        toast.error(
          isVehicleEnroll
            ? VEHICLE_INTEGRATION_ENROLL_ERROR
            : "Failed to update integration status"
        );
        return;
      }

      const action =
        newStatus === IntegrationStatus.Activated ? "activated" : "deactivated";

      toast.success(
        vehicleId
          ? `Vehicle successfully ${action}`
          : `Access Pass successfully ${action}`
      );

      if (setParkerIntegrations) {
        if (!vehicleId) {
          await refreshParkerIntegrations();
        } else {
          setParkerIntegrations((prev) => {
            const keyMatch = (i: ServerRequestsParkerIntegrationPartyDto) =>
              i.integrationPartyId === updatedIntegration!.integrationPartyId &&
              i.parkerId === updatedIntegration.parkerId &&
              i.vehicleId === updatedIntegration!.vehicleId;

            return prev.some(keyMatch)
              ? prev.map((i) =>
                  keyMatch(i) ? { ...i, ...updatedIntegration } : i
                )
              : [...prev, updatedIntegration];
          });
        }
      }
    } catch (err) {
      console.error("Failed to update integration status:", err);
      toast.error(
        isVehicleEnroll
          ? VEHICLE_INTEGRATION_ENROLL_ERROR
          : "Failed to update integration status"
      );
    }
  };

  return {
    vehicleAdd,
    vehicleRemove,
    vehicleLink,
    openChooseModalForParkersList,
    handleToggleIntegration,
    canAddExisting,
  };
}
